function numeric(value) {
  const normalized = String(value ?? "").replaceAll(",", "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizedLabel(value) {
  return String(value ?? "").trim().toLowerCase();
}

export const TRAJECTORY_ENERGY_THRESHOLDS_FOOT_POUNDS = Object.freeze([500, 350]);
export const TRANSONIC_ENTRY_MACH = 1.2;
export const TRANSONIC_EXIT_MACH = 1;
export const TRAJECTORY_CHART_INTERVAL_YARDS = 10;

export function extractTrajectoryTemperatureFahrenheit(result) {
  for (const section of result?.sections || []) {
    for (const table of section.tables || []) {
      for (const row of table.rows || []) {
        const label = normalizedLabel(row.cells?.[0]).replace(/:$/, "");
        const value = numeric(row.cells?.[1]);
        // The trajectory CGI always reports this input-data row in Fahrenheit.
        // Its legacy degree glyph can be mojibake after byte-oriented WASM output
        // decoding, so the stable label is more reliable than the display glyph.
        if (label === "temperature" && value !== null) return value;
      }
    }
  }
  return null;
}

export function extractTrajectorySeries(result) {
  for (let sectionIndex = 0; sectionIndex < (result?.sections?.length || 0); sectionIndex += 1) {
    const section = result.sections[sectionIndex];
    for (let tableIndex = 0; tableIndex < section.tables.length; tableIndex += 1) {
      const table = section.tables[tableIndex];
      const headerIndex = table.rows.findIndex((row) => row.header
        && row.cells.some((cell) => normalizedLabel(cell) === "range")
        && row.cells.some((cell) => normalizedLabel(cell) === "drop"));
      if (headerIndex < 0) continue;

      const headers = table.rows[headerIndex].cells;
      const unitIndex = table.rows[headerIndex + 1]?.header ? headerIndex + 1 : -1;
      const units = unitIndex >= 0 ? table.rows[unitIndex].cells : [];
      const rangeColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "range");
      const velocityColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "velocity");
      const machColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "mach");
      const energyColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "energy");
      const dropColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "drop");
      const windageColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "windage");
      const spinDriftColumnIndex = headers.findIndex(
        (cell) => normalizedLabel(cell) === "spin drift",
      );
      const coriolisWindageColumnIndex = headers.findIndex(
        (cell) => normalizedLabel(cell) === "coriolis windage",
      );
      const leadColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "lead");
      const timeColumnIndex = headers.findIndex((cell) => normalizedLabel(cell) === "time");
      const firstDataIndex = unitIndex >= 0 ? unitIndex + 1 : headerIndex + 1;
      const samples = table.rows
        .map((row, rowIndex) => ({ row, rowIndex }))
        .filter(({ row, rowIndex }) => rowIndex >= firstDataIndex && !row.header)
        .map(({ row, rowIndex }) => ({
          rowIndex,
          range: numeric(row.cells[rangeColumnIndex]),
          velocity: velocityColumnIndex >= 0
            ? numeric(row.cells[velocityColumnIndex]) : null,
          mach: machColumnIndex >= 0
            ? numeric(row.cells[machColumnIndex]) : null,
          energy: energyColumnIndex >= 0
            ? numeric(row.cells[energyColumnIndex]) : null,
          drop: numeric(row.cells[dropColumnIndex]),
          windage: windageColumnIndex >= 0
            ? numeric(row.cells[windageColumnIndex]) : null,
          spinDrift: spinDriftColumnIndex >= 0
            ? numeric(row.cells[spinDriftColumnIndex]) : null,
          coriolisWindage: coriolisWindageColumnIndex >= 0
            ? numeric(row.cells[coriolisWindageColumnIndex]) : null,
          lead: leadColumnIndex >= 0
            ? numeric(row.cells[leadColumnIndex]) : null,
          time: timeColumnIndex >= 0
            ? numeric(row.cells[timeColumnIndex]) : null,
        }));
      const points = samples
        .filter(({ range, drop }) => range !== null && drop !== null)
        .map(({ range, drop }) => ({ range, drop }));

      if (points.length === 0) continue;
      return {
        sectionIndex,
        tableIndex,
        rangeColumnIndex,
        velocityColumnIndex,
        machColumnIndex,
        energyColumnIndex,
        dropColumnIndex,
        windageColumnIndex,
        spinDriftColumnIndex,
        coriolisWindageColumnIndex,
        leadColumnIndex,
        timeColumnIndex,
        rangeUnit: units[rangeColumnIndex] || "",
        velocityUnit: units[velocityColumnIndex] || "",
        machUnit: units[machColumnIndex] || "",
        energyUnit: units[energyColumnIndex] || "",
        dropUnit: units[dropColumnIndex] || "",
        windageUnit: units[windageColumnIndex] || "",
        spinDriftUnit: units[spinDriftColumnIndex] || "",
        coriolisWindageUnit: units[coriolisWindageColumnIndex] || "",
        leadUnit: units[leadColumnIndex] || "",
        timeUnit: units[timeColumnIndex] || "",
        samples,
        points,
      };
    }
  }
  return null;
}

function findDownwardThresholdCrossing(samples, valueKey, thresholdValue) {
  if (thresholdValue === null || thresholdValue === undefined
    || (typeof thresholdValue === "string" && thresholdValue.trim() === "")) {
    return null;
  }
  const threshold = Number(thresholdValue);
  const values = (samples || []).filter((sample) => (
    Number.isFinite(sample.range) && Number.isFinite(sample[valueKey])
  ));
  if (!Number.isFinite(threshold) || values.length === 0) return null;

  for (let index = 0; index < values.length; index += 1) {
    const current = values[index];
    if (current[valueKey] > threshold) continue;
    if (index === 0) {
      return {
        range: current.range,
        followingRowIndex: current.rowIndex,
        threshold,
      };
    }

    const previous = values[index - 1];
    const valueSpan = previous[valueKey] - current[valueKey];
    const fraction = valueSpan > 0
      ? Math.min(1, Math.max(0, (previous[valueKey] - threshold) / valueSpan))
      : 1;
    return {
      range: previous.range + (current.range - previous.range) * fraction,
      followingRowIndex: current.rowIndex,
      threshold,
    };
  }
  return null;
}

function contiguousFiniteSamples(samples, valueKey) {
  const runs = [];
  let current = [];
  for (const sample of samples || []) {
    if (Number.isFinite(sample?.range) && Number.isFinite(sample?.[valueKey])) {
      current.push(sample);
    } else if (current.length > 0) {
      runs.push(current);
      current = [];
    }
  }
  if (current.length > 0) runs.push(current);
  return runs;
}

function intervalOverlapsRegion(previousRange, currentRange, startRange, endRange) {
  const low = Math.min(previousRange, currentRange);
  const high = Math.max(previousRange, currentRange);
  const overlapStart = Math.max(low, startRange);
  const overlapEnd = Math.min(high, endRange);
  return overlapStart < overlapEnd
    || (currentRange >= startRange && currentRange <= endRange);
}

/**
 * Locate the local-atmosphere Mach 1.2-to-1.0 region using the full returned
 * trajectory. Boundary ranges and velocities are linearly interpolated
 * between adjacent samples. A missing boundary is represented by the first
 * or last available sample while its `reached` property remains false.
 */
export function findTrajectoryTransonicRegion(samples,
  entryMach = TRANSONIC_ENTRY_MACH, exitMach = TRANSONIC_EXIT_MACH) {
  const entryThreshold = Number(entryMach);
  const exitThreshold = Number(exitMach);
  const runs = contiguousFiniteSamples(samples, "mach");
  if (!Number.isFinite(entryThreshold) || !Number.isFinite(exitThreshold)
    || entryThreshold <= exitThreshold || runs.length === 0) return null;

  const values = runs.find((run) => {
    const reachesEntry = run.some(({ mach }) => mach <= entryThreshold);
    const beginsInside = run[0].mach <= entryThreshold && run[0].mach > exitThreshold;
    return reachesEntry && (beginsInside || run.some(({ mach }) => mach > entryThreshold));
  });
  if (!values) return null;

  const entryIndex = values.findIndex(({ mach }) => mach <= entryThreshold);
  if (entryIndex < 0 || (entryIndex === 0 && values[0].mach <= exitThreshold)) {
    return null;
  }

  const interpolatedBoundary = (index, threshold) => {
    const current = values[index];
    if (index === 0) {
      return {
        range: current.range,
        velocity: Number.isFinite(current.velocity) ? current.velocity : null,
        followingRowIndex: current.rowIndex,
        reached: current.mach === threshold,
      };
    }
    const previous = values[index - 1];
    const machSpan = previous.mach - current.mach;
    const fraction = machSpan > 0
      ? Math.min(1, Math.max(0, (previous.mach - threshold) / machSpan))
      : 1;
    const velocity = Number.isFinite(previous.velocity) && Number.isFinite(current.velocity)
      ? previous.velocity + (current.velocity - previous.velocity) * fraction
      : null;
    return {
      range: previous.range + (current.range - previous.range) * fraction,
      velocity,
      followingRowIndex: current.rowIndex,
      reached: true,
    };
  };

  const entryBoundary = interpolatedBoundary(entryIndex, entryThreshold);
  const exitIndex = values.findIndex(({ mach }, index) => (
    index >= entryIndex && mach <= exitThreshold
  ));
  const exitBoundary = exitIndex >= 0
    ? interpolatedBoundary(exitIndex, exitThreshold)
    : {
      range: values.at(-1).range,
      velocity: Number.isFinite(values.at(-1).velocity)
        ? values.at(-1).velocity : null,
      followingRowIndex: null,
      reached: false,
    };
  const coveredRowIndices = values.flatMap((sample, index) => {
    const previousRange = index > 0 ? values[index - 1].range : sample.range;
    return intervalOverlapsRegion(previousRange, sample.range,
      entryBoundary.range, exitBoundary.range) ? [sample.rowIndex] : [];
  });

  return {
    kind: "transonic-region",
    entry: {
      mach: entryThreshold,
      thresholdMach: entryThreshold,
      ...entryBoundary,
    },
    exit: {
      mach: exitThreshold,
      thresholdMach: exitThreshold,
      ...exitBoundary,
    },
    startsInsideRegion: !entryBoundary.reached,
    endsInsideRegion: !exitBoundary.reached,
    coveredRowIndices,
    lastRowIndex: values.at(-1).rowIndex,
  };
}

export function findTrajectoryEnergyThreshold(samples, thresholdEnergy) {
  const crossing = findDownwardThresholdCrossing(samples, "energy", thresholdEnergy);
  return crossing ? {
    range: crossing.range,
    followingRowIndex: crossing.followingRowIndex,
    thresholdEnergy: crossing.threshold,
  } : null;
}

export function extractTrajectoryTransonicRegion(result, series = null) {
  const trajectorySeries = series || extractTrajectorySeries(result);
  if (!trajectorySeries || trajectorySeries.machColumnIndex < 0) return null;
  const region = findTrajectoryTransonicRegion(trajectorySeries.samples);
  if (!region) return null;
  return {
    ...region,
    sectionIndex: trajectorySeries.sectionIndex,
    tableIndex: trajectorySeries.tableIndex,
    rangeColumnIndex: trajectorySeries.rangeColumnIndex,
    velocityColumnIndex: trajectorySeries.velocityColumnIndex,
    machColumnIndex: trajectorySeries.machColumnIndex,
  };
}

export function extractTrajectoryEnergyMarkers(result, series = null,
  thresholds = TRAJECTORY_ENERGY_THRESHOLDS_FOOT_POUNDS) {
  const trajectorySeries = series || extractTrajectorySeries(result);
  if (!trajectorySeries || trajectorySeries.energyColumnIndex < 0) return [];
  return thresholds.flatMap((thresholdEnergy) => {
    const crossing = findTrajectoryEnergyThreshold(
      trajectorySeries.samples, thresholdEnergy);
    return crossing ? [{
      ...crossing,
      kind: "energy",
      sectionIndex: trajectorySeries.sectionIndex,
      tableIndex: trajectorySeries.tableIndex,
      rangeColumnIndex: trajectorySeries.rangeColumnIndex,
      energyColumnIndex: trajectorySeries.energyColumnIndex,
    }] : [];
  });
}

export function extractTrajectoryIndicators(result, series = null) {
  const trajectorySeries = series || extractTrajectorySeries(result);
  if (!trajectorySeries) return { transonicRegion: null, energyMarkers: [] };
  return {
    transonicRegion: extractTrajectoryTransonicRegion(result, trajectorySeries),
    energyMarkers: extractTrajectoryEnergyMarkers(result, trajectorySeries)
      .sort((left, right) => left.range - right.range),
  };
}

/**
 * Reduce an engine trajectory to chart-sized canonical-yard samples.
 *
 * The source order and source objects are preserved. The first and last valid
 * points are always retained, while intermediate points are selected at each
 * interval boundary. Marker extraction deliberately operates on the original
 * full sample set instead of this presentation-only subset.
 */
export function decimateTrajectoryChartPoints(
  points, intervalYards = TRAJECTORY_CHART_INTERVAL_YARDS,
) {
  const interval = Number(intervalYards);
  if (!Number.isFinite(interval) || interval <= 0) {
    throw new RangeError("Chart interval must be a positive finite number.");
  }

  const values = (Array.isArray(points) ? points : []).filter(({ range, drop }) => (
    Number.isFinite(range) && Number.isFinite(drop)
  ));
  if (values.length < 2) return [...values];

  const selected = [values[0]];
  let nextRange = values[0].range + interval;
  for (let index = 1; index < values.length - 1; index += 1) {
    const point = values[index];
    if (point.range < nextRange) continue;
    selected.push(point);
    while (nextRange <= point.range) nextRange += interval;
  }

  const last = values.at(-1);
  if (selected.at(-1) !== last) selected.push(last);
  return selected;
}

/** Select the post-zero samples that define an angular chart's vertical scale. */
export function trajectoryChartScalePoints(points, {
  angular = false,
  zeroRangeYards = null,
} = {}) {
  const values = (Array.isArray(points) ? points : []).filter(({ range, drop }) => (
    Number.isFinite(range) && Number.isFinite(drop)
  ));
  const zeroRange = Number(zeroRangeYards);
  const unchanged = () => ({
    points: [...values],
    postZeroScale: false,
    zeroRangeYards: Number.isFinite(zeroRange) ? zeroRange : null,
    omittedPointCount: 0,
  });
  if (!angular || !Number.isFinite(zeroRange) || zeroRange <= 0 || values.length === 0) {
    return unchanged();
  }

  const tolerance = Math.max(1, Math.abs(zeroRange)) * 1e-9;
  const postZeroPoints = values.filter(({ range }) => range + tolerance >= zeroRange);
  if (postZeroPoints.length === 0) return unchanged();

  return {
    points: postZeroPoints,
    postZeroScale: true,
    zeroRangeYards: zeroRange,
    omittedPointCount: values.length - postZeroPoints.length,
  };
}

function chartTicks(minimum, maximum, count) {
  return Array.from({ length: count }, (_, index) => (
    minimum + (maximum - minimum) * index / (count - 1)
  ));
}

export function buildTrajectoryChartGeometry(points, options = {}) {
  const width = options.width || 900;
  const height = options.height || 360;
  const padding = { top: 22, right: 24, bottom: 54, left: 72 };
  const values = points.filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y));
  if (values.length < 2) return null;
  const suppliedYDomain = Array.isArray(options.yDomainPoints)
    ? options.yDomainPoints.filter(({ y }) => Number.isFinite(y)) : [];
  const yDomainValues = suppliedYDomain.length > 0 ? suppliedYDomain : values;

  const xMin = Math.min(...values.map(({ x }) => x));
  let xMax = Math.max(...values.map(({ x }) => x));
  let yMin = Math.min(0, ...yDomainValues.map(({ y }) => y));
  let yMax = Math.max(0, ...yDomainValues.map(({ y }) => y));
  if (xMin === xMax) xMax = xMin + 1;
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  } else {
    const margin = (yMax - yMin) * 0.06;
    yMin -= margin;
    yMax += margin;
  }

  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const x = (value) => padding.left + (value - xMin) / (xMax - xMin) * plotWidth;
  const y = (value) => padding.top + (yMax - value) / (yMax - yMin) * plotHeight;
  const path = values.map((point, index) => (
    `${index === 0 ? "M" : "L"}${x(point.x).toFixed(2)},${y(point.y).toFixed(2)}`
  )).join(" ");

  return {
    width,
    height,
    padding,
    xMin,
    xMax,
    yMin,
    yMax,
    path,
    zeroY: y(0),
    xTicks: chartTicks(xMin, xMax, 6).map((value) => ({ value, position: x(value) })),
    yTicks: chartTicks(yMin, yMax, 5).map((value) => ({ value, position: y(value) })),
  };
}

export function chartXPosition(geometry, value) {
  const numericValue = Number(value);
  if (!geometry || !Number.isFinite(numericValue)
    || numericValue < geometry.xMin || numericValue > geometry.xMax) return null;
  const plotWidth = geometry.width - geometry.padding.left - geometry.padding.right;
  return geometry.padding.left
    + (numericValue - geometry.xMin) / (geometry.xMax - geometry.xMin) * plotWidth;
}

export function formatChartTick(value) {
  const absolute = Math.abs(value);
  if (absolute >= 1000) return value.toFixed(0);
  if (absolute >= 100) return value.toFixed(1).replace(/\.0$/, "");
  if (absolute >= 10) return value.toFixed(2).replace(/\.0+$/, "");
  return value.toFixed(3).replace(/\.?0+$/, "") || "0";
}
